import {WebMails} from "../models/webmail.model";
import {ZDocumentType} from "../models/document.model";

const API_URL = "https://zlawyerapi.azurewebsites.net";

const premiereDefinie = (objet: any, ...cles: string[]) => {
    if (objet == null || typeof objet !== "object") {
        return undefined;
    }
    for (const cle of cles) {
        if (objet[cle] !== undefined && objet[cle] !== null) {
            return objet[cle];
        }
    }
    return undefined;
};

const extraireListe = (reponse: any): any[] => {
    if (Array.isArray(reponse)) {
        return reponse;
    }
    const liste = premiereDefinie(reponse, "datas", "Datas", "listeForJson", "ListeForJson");
    return Array.isArray(liste) ? liste : [];
};

const normaliserDossier = (dossier: any) => ({
    ...dossier,
    IdDossier: premiereDefinie(dossier, "idDossier", "IdDossier"),
    Nom: premiereDefinie(dossier, "nom", "Nom") ?? "",
});

const normaliserAssociation = (association: any) => ({
    ...association,
    idDossier: premiereDefinie(association, "idDossier", "IdDossier"),
    email: premiereDefinie(association, "email", "Email"),
});

const normaliserNoeudFusion = (noeud: any): any => ({
    ...noeud,
    IdWordChampChapitre: premiereDefinie(noeud, "idWordChampChapitre", "IdWordChampChapitre"),
    ValeurWordChampChapitre: premiereDefinie(noeud, "valeurWordChampChapitre", "ValeurWordChampChapitre"),
    IdWordChampsRubrique: premiereDefinie(noeud, "idWordChampsRubrique", "IdWordChampsRubrique"),
    ValeurWordChampRubrique: premiereDefinie(noeud, "valeurWordChampRubrique", "ValeurWordChampRubrique"),
    rubrique: (Array.isArray(premiereDefinie(noeud, "rubrique", "Rubrique"))
        ? premiereDefinie(noeud, "rubrique", "Rubrique")
        : []).map(normaliserNoeudFusion),
});

const normaliserChampWord = (champ: any) => ({
    ...champ,
    Champ: premiereDefinie(champ, "champ", "Champ"),
    Libelle: premiereDefinie(champ, "libelle", "Libelle"),
});

const normaliserReferentielChamps = (reponse: any) => {
    const source = premiereDefinie(reponse, "datas", "Datas") ?? reponse;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
        return reponse;
    }
    return Object.fromEntries(
        Object.entries(source).map(([cle, valeur]) => [
            cle,
            Array.isArray(valeur) ? valeur.map(normaliserChampWord) : valeur,
        ])
    );
};

const versCamelCase = (valeur: any): any => {
    if (Array.isArray(valeur)) {
        return valeur.map(versCamelCase);
    }
    if (valeur && typeof valeur === "object" && !(valeur instanceof Date)) {
        return Object.fromEntries(
            Object.entries(valeur).map(([cle, val]) => [
                cle.charAt(0).toLowerCase() + cle.slice(1),
                versCamelCase(val),
            ])
        );
    }
    return valeur;
};

const zLawyerApiTexte = async (endpoint: string, body: any) => {
    const headers: Record<string, string> = {"Content-Type": "application/json"};
    const token = localStorage.getItem("zLawyerToken");
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(versCamelCase(body)),
    });
    return await response.text();
}

const zLawyerApi = async (endpoint: string, methode: string = "POST", body: any | null = null) => {
    const request = {
        method: methode,
        headers: {"Content-Type": "text/json; charset=UTF-8"},
    };
    if (localStorage.getItem("zLawyerToken")) {
        request.headers['Authorization'] = `Bearer ${localStorage.getItem("zLawyerToken")}`;
    }
    if (methode === "POST") {
        request['body'] = JSON.stringify(versCamelCase(body));
    }
    const response = await fetch(endpoint, request);
    return await response.json();
}

export const useUserService = () => {
    const connexionParToken = async () => {
        return await zLawyerApi(`${API_URL}/authentification/contexte`, "GET");
    }

    return {
        connexionParToken
    };
};

export const useMailService = () => {
    const MAIL_SERVICE_URL = API_URL;

    const DEMANDE_AUTHENTIFICATION = "authentification/demande-authentification";
    const VERIFIER_AUTHENTIFICATION = "authentification/verifier-authentification";
    const ENREGISTRE_WEBMAIL = "mails";
    const DONNE_LISTE_DOSSIERS_ASSOCIE_EMAIL = "mails/associations";
    const DONNE_LISTE_MAILS_ASSOCIATIONS = "dossiers/associations";
    const ENREGISTRE_MAILS_ASSOCIATIONS = "mails/associations";

    const demandeAuthentification = async () => {
        return await zLawyerApi(`${MAIL_SERVICE_URL}/${DEMANDE_AUTHENTIFICATION}`, "POST", {idDemande: localStorage.getItem("zLawyerDemande")});
    }

    const verifierAuthentification = async () => {
        return await zLawyerApi(`${MAIL_SERVICE_URL}/${VERIFIER_AUTHENTIFICATION}?idDemande=${localStorage.getItem("zLawyerDemande")}`, "GET");
    }

    const enregistreWebMail = async (webMail: WebMails) => {
        return await zLawyerApi(`${MAIL_SERVICE_URL}/${ENREGISTRE_WEBMAIL}`, "POST", webMail);
    }

    const donneListeDossiersAssocieEmail = async (email: string) => {
        const reponse = await zLawyerApi(`${MAIL_SERVICE_URL}/${DONNE_LISTE_DOSSIERS_ASSOCIE_EMAIL}?email=${email}`, "GET");
        return {listeForJson: extraireListe(reponse).map(normaliserAssociation)};
    }

    const donneListeMailsAssociations = async (idDossier: number) => {
        const reponse = await zLawyerApi(`${MAIL_SERVICE_URL}/${DONNE_LISTE_MAILS_ASSOCIATIONS}/${idDossier}`, "GET");
        return {listeForJson: extraireListe(reponse).map(normaliserAssociation)};
    }

    const enregistreMailsAssociations = async (email: string, idDossier: number) => {
        return await zLawyerApi(`${MAIL_SERVICE_URL}/${ENREGISTRE_MAILS_ASSOCIATIONS}`, "POST", {
            email: email,
            idDossier: idDossier
        });
    }

    return {
        demandeAuthentification,
        verifierAuthentification,
        enregistreWebMail,
        donneListeDossiersAssocieEmail,
        donneListeMailsAssociations,
        enregistreMailsAssociations
    };
};

export const useDossierService = () => {
    const donneListeDossiers = async () => {
        const reponse = await zLawyerApi(`${API_URL}/api/v2/dossiers`, "GET");
        return {listeForJson: extraireListe(reponse).map(normaliserDossier)};
    }

    return {
        donneListeDossiers
    };
};

export const useChampsFusionService = () => {
    const champsFusion = async (type?: ZDocumentType) => {
        const relance = type === ZDocumentType.MODELE_RELANCE;
        const reponse = await zLawyerApi(`${API_URL}/api/v2/office/champs-fusion?relance=${relance}`, "GET");
        return {listeForJson: extraireListe(reponse).map(normaliserNoeudFusion)};
    }

    return {
        champsFusion
    };
};

export const useAssistantService = () => {
    const ASSISTANT_URL = `${API_URL}/api/v2/assistant`;

    const chat = async (prompt: string) => {
        return await zLawyerApiTexte(`${ASSISTANT_URL}/chat`, {
            idConversation: "0",
            prompt: prompt,
        });
    }

    return {chat};
};

export const useInsertionWordService = () => {
    const OFFICE_URL = `${API_URL}/api/v2/office`;

    const champsInsererWord = async () => {
        const reponse = await zLawyerApi(`${OFFICE_URL}/champs-word`, "GET");
        return normaliserReferentielChamps(reponse);
    }

    const valeursInsererWord = async (idDossier: number, rubrique: string) => {
        const reponse = await zLawyerApi(`${OFFICE_URL}/valeurs-word?idDossier=${idDossier}&rubrique=${encodeURIComponent(rubrique)}`, "GET");
        return {
            listeForJson: extraireListe(reponse).map((item) => ({
                ...item,
                Id: premiereDefinie(item, "id", "Id"),
                Libelle: premiereDefinie(item, "libelle", "Libelle"),
            })),
        };
    }

    return {
        champsInsererWord,
        valeursInsererWord
    };
};
